import React, { useEffect, useState, useCallback } from 'react';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Button } from '../ui/button';
import { LifeBuoy, AlertTriangle } from 'lucide-react';
import api from '../../utils/api';
import BackupRoleNoticeModal from '../BackupRoleNoticeModal';

/**
 * BackupAckBanner - Iter-133x (P0)
 *
 * Dashboard banner shown whenever the talent holds at least one
 * `backup` booking that has not been acknowledged for the current
 * notice version. Clicking "Acknowledge backup role" opens the
 * blocking BackupRoleNoticeModal for the first outstanding booking,
 * and the banner shrinks as each is acknowledged.
 *
 * Backed by GET /api/talent/backup-acknowledgments/pending which
 * already filters out acknowledged / past / non-active bookings.
 */
const BackupAckBanner = () => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeBookingId, setActiveBookingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/talent/backup-acknowledgments/pending')
      .then((res) => setPending(res.data?.bookings || []))
      .catch(() => setPending([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || pending.length === 0) return null;

  const first = pending[0];

  return (
    <>
      <Alert
        className="mb-6 bg-amber-50 border-amber-300"
        data-testid="backup-ack-banner"
      >
        <AlertTriangle className="h-4 w-4 text-amber-700" />
        <AlertTitle className="text-amber-900 font-semibold flex items-center gap-2">
          <LifeBuoy className="w-4 h-4" />
          Backup role acknowledgment required
        </AlertTitle>
        <AlertDescription className="text-amber-800 mt-1">
          <div className="text-sm mb-2">
            You are assigned as {first.backup_mode === 'on_call' ? 'on-call' : 'on-site'} backup talent for <strong>{first.venue_name}</strong>
            {first.city ? ` in ${first.city}, ${first.state}` : ''}.
            You must review and agree to the backup role terms before this booking is active.
            {pending.length > 1 && (
              <span className="block text-xs text-amber-700 mt-1">
                +{pending.length - 1} more backup booking{pending.length > 2 ? 's' : ''} also awaiting acknowledgment.
              </span>
            )}
          </div>
          <Button
            size="sm"
            className="bg-amber-600 hover:bg-amber-700 text-white"
            onClick={() => setActiveBookingId(first.booking_id)}
            data-testid="backup-ack-open-modal-btn"
          >
            <LifeBuoy className="w-4 h-4 mr-2" />
            Acknowledge backup role
          </Button>
        </AlertDescription>
      </Alert>

      {activeBookingId && (
        <BackupRoleNoticeModal
          open={true}
          bookingId={activeBookingId}
          mode={pending.find(b => b.booking_id === activeBookingId)?.backup_mode || 'on_site'}
          onAcknowledged={load}
          onClose={() => setActiveBookingId(null)}
        />
      )}
    </>
  );
};

export default BackupAckBanner;
